import { Country } from 'APIs/fragments';
import { MerchantBusinessType, TaxCountry } from 'APIs/merchants/merchants.types';
import { countries, merchantBusinessTypes, merchantTaxCountries } from 'Constants/Constants';
import { isPossiblePhoneNumber } from 'react-phone-number-input';
import * as Yup from 'yup';

const FILE_SIZE_LIMIT = 10 * 1024 * 1024; // 10MB

export const validationSchema = Yup.object().shape({
  businessName: Yup.string().trim().required('Business name is required'),
  brandName: Yup.string()
    .trim()
    .min(3, 'Brand name must be at least 3 characters')
    .max(100, 'Brand name must be at most 100 characters'),
  website: Yup.string().trim().url('Must be a valid URL').required('Website url is required'),
  supportSite: Yup.string().trim().url('Must be a valid URL').required('Support site url is required'),
  businessType: Yup.string().oneOf(
    merchantBusinessTypes.map((b) => b.value as MerchantBusinessType),
    'Business type is required',
  ),
  companyType: Yup.string().nullable(),
  industry: Yup.string().trim().required('Industry is required'),
  timeZone: Yup.string().trim().required('Time zone is required'),
  taxCountry: Yup.string().oneOf(merchantTaxCountries.map((c) => c.value as TaxCountry)),
  taxId: Yup.string().trim().required('Tax ID is required'),
  contactEmailAddress: Yup.string().trim().email('Must be a valid email').required('Contact email is required'),
  supportEmailAddress: Yup.string().trim().email('Must be a valid email').nullable(),
  phoneNumber: Yup.string()
    .trim()
    .required('Phone number is required')
    .test({
      name: 'phone-no-validation',
      test: function () {
        const { phoneNumber } = this.parent;
        if (!phoneNumber)
          return this.createError({
            message: `Phone number is required`,
            path: `phoneNumber`,
          });
        if (!isPossiblePhoneNumber(phoneNumber))
          return this.createError({
            message: `Phone number is invalid`,
            path: `phoneNumber`,
          });
        return true;
      },
    }),
  cardStatementName: Yup.string()
    .trim()
    .min(5, 'Card statement name must be at least 5 characters')
    .max(13, 'Card statement name must be at most 13 characters')
    .matches(/^[A-Za-z0-9\s]+$/, 'Card statement name can only contain letters, numbers and spaces'),
  smsSenderName: Yup.string()
    .trim()
    .min(3, 'SMS sender name must be at least 3 characters')
    .max(11, 'SMS sender name must be 11 characters or less'),
  contactPerson: Yup.object().shape({
    title: Yup.string().trim().required('Title is required'),
    givenName: Yup.string().trim().required('Given name is required'),
    familyName: Yup.string().trim().required('Family name is required'),
  }),
  address: Yup.object().shape({
    unitNumber: Yup.string().trim().nullable(),
    streetNumber: Yup.string().trim().required('Street number is required'),
    streetName: Yup.string().trim().required('Street name is required'),
    streetType: Yup.string().trim().required('Street type is required'),
    suburb: Yup.string().trim().nullable(),
    city: Yup.string().when('country', {
      is: (country: Country) => !['AU', 'US'].includes(country),
      then: Yup.string().trim().required('City is required').nullable(),
      otherwise: Yup.string().trim().notRequired().nullable(),
    }),
    postalCode: Yup.string().trim().required('Postal code is required'),
    state: Yup.string().when('country', {
      is: (country: Country) => ['AU', 'US'].includes(country),
      then: Yup.string().trim().required('State is required').nullable(),
      otherwise: Yup.string().trim().notRequired().nullable(),
    }),
    country: Yup.string().oneOf(countries.map((c) => c.value)),
  }),
  verificationDocs: Yup.object().shape({
    partnershipAgreementFile: Yup.mixed().test('fileSize', `File too big, can't exceed 10MB limit`, (value) => {
      if (value) return value.size <= FILE_SIZE_LIMIT;
      else return true;
    }),
    trustDeedFile: Yup.mixed().test('fileSize', `File too big, can't exceed 10MB limit`, (value) => {
      if (value) return value.size <= FILE_SIZE_LIMIT;
      else return true;
    }),
    trustDeedAmendmentFile: Yup.mixed().test('fileSize', `File too big, can't exceed 10MB limit`, (value) => {
      if (value) {
        return value.size <= FILE_SIZE_LIMIT;
      } else return true;
    }),
  }),
});
